<template>
  <div class="page">
    <div class="container">
      <div class="page-header">
        <h1>LO Tracker</h1>
      </div>

      <div class="view-controls">
        <div class="view-buttons">
          <Select v-model="filterCongress" :options="congressFilterOptions" optionLabel="label" optionValue="value" placeholder="All Congress" showClear class="filter-select" />
          <Button :label="'Table'" size="small" :severity="viewMode === 'table' ? undefined : 'secondary'" icon="pi pi-list" @click="viewMode = 'table'" />
          <Button :label="'Two Weeks'" size="small" :severity="viewMode === 'twoweeks' ? undefined : 'secondary'" icon="pi pi-calendar" @click="viewMode = 'twoweeks'" />
          <Button :label="'Month'" size="small" :severity="viewMode === 'month' ? undefined : 'secondary'" icon="pi pi-calendar-plus" @click="viewMode = 'month'" />
          <Button :label="'Metrics'" size="small" :severity="viewMode === 'metrics' ? undefined : 'secondary'" icon="pi pi-chart-bar" @click="viewMode = 'metrics'" />
        </div>
      </div>

      <!-- Table View -->
      <DataTable 
        v-if="viewMode === 'table'"
        :value="filteredRecords" 
        :paginator="true" 
        :rows="10"
        :loading="loading"
        stripedRows
        showGridlines
        v-model:filters="filters"
        filterDisplay="menu"
        scrollable
        scrollHeight="600px"
      >
        <template #header>
          <div class="table-filters">
            <div class="filters-grid">
              <IconField iconPosition="left" class="search-field">
                <InputIcon class="pi pi-search" />
                <InputText v-model="filters['global'].value" placeholder="Search all columns..." />
              </IconField>
              <Select v-model="filterRequestingParty" :options="loRequestingPartyOptions" optionLabel="label" optionValue="value" placeholder="Requesting Party" showClear class="filter-item" />
              <Select v-model="filterCommittee" :options="loCommitteeOptions" optionLabel="label" optionValue="value" placeholder="Committee" showClear class="filter-item" filter />
              <Select v-model="filterLoggedBy" :options="loggedByOptions" optionLabel="label" optionValue="value" placeholder="Logged By" showClear class="filter-item" />
              <Select v-model="filterLO" :options="loOfficerOptions" optionLabel="label" optionValue="value" placeholder="Assigned LO" showClear class="filter-item" />
              <Select v-model="filterAO" :options="aoOfficerOptions" optionLabel="label" optionValue="value" placeholder="Assigned AO" showClear class="filter-item" />
              <Select v-model="filterSubjectTitle" :options="loSubjectTitleOptions" optionLabel="label" optionValue="value" placeholder="Subject Title" showClear class="filter-item" filter />
              <Select v-model="filterStatus" :options="loStatusOptions" optionLabel="label" optionValue="value" placeholder="Status" showClear class="filter-item" />
            </div>
            <div class="filter-summary">
              <span class="result-count">{{ filteredRecords.length }} record(s)</span>
              <Button v-if="hasActiveLoFilters" label="Clear Filters" icon="pi pi-filter-slash" size="small" severity="secondary" text @click="clearLoFilters" />
            </div>
          </div>
        </template>
        <Column field="hearing_date_raw" header="Hearing Date" sortable style="min-width: 110px">
          <template #body="{ data }">{{ data.hearing_date || '—' }}</template>
        </Column>
        <Column field="agenda" header="Agenda" sortable style="min-width: 150px">
          <template #body="{ data }">
            <span v-if="data.agenda" style="white-space: pre-line;">{{ data.agenda }}</span>
            <span v-else class="text-muted">—</span>
          </template>
        </Column>
        <Column field="requesting_party" header="Requesting Party" sortable style="min-width: 140px" />
        <Column header="Actual Attendees" style="min-width: 200px">
          <template #body="{ data }">
            <div v-if="data.actual_attendees && data.actual_attendees.length" class="attendee-list">
              <div v-for="(att, i) in data.actual_attendees" :key="i" class="attendee-item">
                <span class="attendee-code">{{ att.agency_code }}:<br></span>
                <template v-if="att.names && att.names.toLowerCase() !== 'no attendees'">
                  <span class="attendee-name" style="white-space: pre-line;">{{ att.names }}</span>
                </template>
                <span v-else class="attendee-no">No attendees.</span>
              </div>
            </div>
            <span v-else class="text-muted">—</span>
          </template>
        </Column>
        <Column header="Key Points Discussed" style="min-width: 180px">
          <template #body="{ data }">
            <div v-if="data.key_points_discussed && data.key_points_discussed.length">
              <div v-for="(kp, i) in data.key_points_discussed.slice(0, 2)" :key="i" style="font-size: 0.85rem;">• {{ kp.substring(0, 60) }}{{ kp.length > 60 ? '...' : '' }}</div>
              <span v-if="data.key_points_discussed.length > 2" style="font-size: 0.75rem; color: #64748b;">+{{ data.key_points_discussed.length - 2 }} more</span>
            </div>
            <span v-else class="text-muted">—</span>
          </template>
        </Column>
        <Column field="committee_remarks" header="Committee Remarks" style="min-width: 150px">
          <template #body="{ data }">
            <span v-if="data.committee_remarks" style="font-size: 0.85rem;">{{ data.committee_remarks?.substring(0, 80) }}{{ data.committee_remarks?.length > 80 ? '...' : '' }}</span>
            <span v-else class="text-muted">—</span>
          </template>
        </Column>
        <Column field="dof_actions_to_be_taken" header="DOF Actions" style="min-width: 150px">
          <template #body="{ data }">
            <span v-if="data.dof_actions_to_be_taken && data.dof_actions_to_be_taken.length">{{ Array.isArray(data.dof_actions_to_be_taken) ? data.dof_actions_to_be_taken.join(', ') : data.dof_actions_to_be_taken }}</span>
            <span v-else class="text-muted">—</span>
          </template>
        </Column>
        <Column field="logged_by" header="Logged By" sortable  style="min-width: 130px" />
        <Column field="assigned_lo_name" header="Assigned LO" sortable  style="min-width: 130px" />
        <Column field="account_officer_name" header="Assigned AO" sortable style="min-width: 130px" />
       
        <Column header="Actions" style="min-width: 90px">
          <template #body="{ data }">
            <div class="actions">
              <Button icon="pi pi-pencil" severity="info" size="small" :loading="editLoading === data.id" @click="openEditModal(data)"/>
              <Button v-if="canReassign" icon="pi pi-users" severity="warn" size="small" @click="openReassignModal(data)" v-tooltip="'Re-assign LO'" />
            </div>
          </template>
        </Column>
      </DataTable>

      <!-- Two Weeks View -->
      <div v-if="viewMode === 'twoweeks'" class="calendar-view">
        <h3>This Week & Next Week</h3>
        <div class="week-grid">
          <div v-for="day in loTwoWeekDays" :key="day.date" class="day-card" :class="{ today: day.isToday }">
            <div class="day-header">
              <span class="day-name">{{ day.dayName }}</span>
              <span class="day-date">{{ day.dateFormatted }}</span>
            </div>
            <div class="day-events">
              <div v-for="item in day.items" :key="item.id" class="day-event">
                <div class="event-committee">{{ item.agenda?.substring(0, 30) }}</div>
                <div class="event-time">{{ item.hearing_time }}</div>
              </div>
              <div v-if="day.items.length === 0" class="no-events">No hearings</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Month View -->
      <div v-if="viewMode === 'month'" class="calendar-view">
        <div class="month-header">
          <Button icon="pi pi-chevron-left" size="small" severity="secondary" @click="loPrevMonth" />
          <h3>{{ loCurrentMonthName }}</h3>
          <Button icon="pi pi-chevron-right" size="small" severity="secondary" @click="loNextMonth" />
        </div>
        <div class="month-grid">
          <div class="month-day-header" v-for="d in ['Mon','Tue','Wed','Thu','Fri']" :key="d">{{ d }}</div>
          <div v-for="day in loMonthDays" :key="day.date || Math.random()" class="month-day-cell" :class="{ empty: !day.date, today: day.isToday, 'has-events': day.items && day.items.length > 0 }">
            <div class="month-day-num" v-if="day.date">{{ day.dayNum }}</div>
            <div class="month-day-events" v-if="day.items">
              <div v-for="item in day.items.slice(0, 2)" :key="item.id" class="month-event-dot">{{ item.agenda?.substring(0, 20) }}</div>
              <div v-if="day.items.length > 2" class="month-more">+{{ day.items.length - 2 }} more</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Metrics View -->
      <div v-if="viewMode === 'metrics'" class="metrics-view">
        <div class="metrics-grid">
          <div class="metric-card">
            <h4>By Congress Period</h4>
            <div class="metric-bars">
              <div v-for="item in loMetricsCongress" :key="item.label" class="metric-bar-row">
                <span class="metric-label">{{ item.label }}</span>
                <div class="metric-bar-container"><div class="metric-bar" :style="{ width: item.percent + '%' }"></div></div>
                <span class="metric-count">{{ item.count }}</span>
              </div>
            </div>
          </div>
          <div class="metric-card">
            <h4>By Requesting Party</h4>
            <div class="metric-bars">
              <div v-for="item in loMetricsRequestingParty" :key="item.label" class="metric-bar-row">
                <span class="metric-label">{{ item.label }}</span>
                <div class="metric-bar-container"><div class="metric-bar pie-bar" :style="{ width: item.percent + '%' }"></div></div>
                <span class="metric-count">{{ item.count }}</span>
              </div>
            </div>
          </div>
          <div class="metric-card">
            <h4>By Committee (Top 10)</h4>
            <div class="metric-bars">
              <div v-for="item in loMetricsCommittee" :key="item.label" class="metric-bar-row">
                <span class="metric-label" :title="item.label">{{ item.label?.substring(0, 30) }}{{ item.label?.length > 30 ? '...' : '' }}</span>
                <div class="metric-bar-container"><div class="metric-bar stacked-bar" :style="{ width: item.percent + '%' }"></div></div>
                <span class="metric-count">{{ item.count }}</span>
              </div>
            </div>
          </div>
          <div class="metric-card">
            <h4>By Liaison Officer</h4>
            <div class="metric-bars">
              <div v-for="item in loMetricsLO" :key="item.label" class="metric-bar-row">
                <span class="metric-label">{{ item.label }}</span>
                <div class="metric-bar-container"><div class="metric-bar logged-bar" :style="{ width: item.percent + '%' }"></div></div>
                <span class="metric-count">{{ item.count }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Dialog v-model:visible="showModal" header="Record Details" :modal="true" :maximizable="true" :style="{width: '100vw', height: '100vh'}" :contentStyle="{height: '100%'}" position="top">
      <div class="detail-grid">
        <!-- Read-only fields -->
        <div class="detail-row">
          <div class="detail-item">
            <span class="detail-label">Congress No.</span>
            <span class="detail-value">{{ currentRecord.congress_period }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Date Received</span>
            <span class="detail-value">{{ currentRecord.date_received }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Hearing Date</span>
            <span class="detail-value">{{ currentRecord.hearing_date || '—' }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Hearing Time</span>
            <span class="detail-value">{{ currentRecord.hearing_time || '—' }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Hearing Place</span>
            <span class="detail-value">{{ currentRecord.hearing_place || '—' }}</span>
          </div>
        </div>
        <div class="detail-row">
          <div class="detail-item">
            <span class="detail-label">Agenda</span>
            <span class="detail-value">{{ currentRecord.agenda || '—' }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Agenda Link</span>
            <span class="detail-value">
              <a v-if="currentRecord.agenda_link" :href="currentRecord.agenda_link" target="_blank" rel="noopener noreferrer">{{ currentRecord.agenda_link }}</a>
              <span v-else>—</span>
            </span>
          </div>
        </div>
        <div class="detail-row">
          <div class="detail-item">
            <span class="detail-label">Referrals</span>
            <span class="detail-value">{{ formatReferrals(currentRecord.referrals) }}</span>
          </div>
        </div>

        <!-- Editable fields -->
        <div class="editable-section">
          <div class="form-group">
            <FloatLabel variant="in">
              <InputText id="edit_long_title" v-model="currentRecord.long_title"/>
              <label for="edit_long_title">Long Title</label>
            </FloatLabel>
          </div>
          <div class="form-group">
            <FloatLabel variant="in">
              <InputText id="edit_short_title" v-model="currentRecord.short_title"/>
              <label for="edit_short_title">Short Title</label>
            </FloatLabel>
          </div>
          <div class="form-row-2">
			<div class="form-group">
              <FloatLabel variant="in">
                <MultiSelect id="edit_bill_category" v-model="currentRecord.bill_category" :options="billCategoryOptions" optionLabel="label" optionValue="value" display="chip" />
                <label for="edit_bill_category">Bill Category</label>
              </FloatLabel>
            </div>
            <div class="form-group">
              <FloatLabel variant="in">
                <MultiSelect id="edit_dof_actions" v-model="currentRecord.dof_actions_to_be_taken" :options="dofActionsOptions" optionLabel="label" optionValue="value" filter display="chip" />
                <label for="edit_dof_actions">DOF Actions to be Taken</label>
              </FloatLabel>
            </div>
          </div>
          <div class="form-row-3">
            <div class="form-group">
              <FloatLabel variant="in">
                <Select id="edit_dof_position" v-model="currentRecord.dof_position" :options="dofPositionOptions" optionLabel="label" optionValue="value" filter />
                <label for="edit_dof_position">DOF Position</label>
              </FloatLabel>
            </div>
            <div class="form-group">
              <FloatLabel variant="in">
                <DatePicker id="edit_deadline" v-model="currentRecord.deadline_of_deliverables" dateFormat="yy-mm-dd" />
                <label for="edit_deadline">Deadline of Deliverables</label>
              </FloatLabel>
            </div>
            <div class="form-group">
              <FloatLabel variant="in">
                <Select id="edit_status_deliverables" v-model="currentRecord.status_of_dof_deliverables" :options="statusOfDeliverablesOptions" optionLabel="label" optionValue="value" />
                <label for="edit_status_deliverables">Status of DOF Deliverables</label>
              </FloatLabel>
            </div>
          </div>
          <div class="form-group">
            <FloatLabel variant="in">
              <Textarea id="edit_additional_remarks" v-model="currentRecord.additional_remarks" rows="2"/>
              <label for="edit_additional_remarks">Additional Remarks</label>
            </FloatLabel>
          </div>
          <div class="form-group">
            <FloatLabel variant="in">
              <Textarea id="edit_pending_tasks" v-model="currentRecord.pending_tasks" rows="2"/>
              <label for="edit_pending_tasks">Pending Tasks/Requirements</label>
            </FloatLabel>
          </div>
          <div class="form-group">
            <FloatLabel variant="in">
              <Select id="edit_type_of_meeting" v-model="currentRecord.type_of_meeting" :options="typeOfMeetingOptions" optionLabel="label" optionValue="value" />
              <label for="edit_type_of_meeting">Type of Meeting</label>
            </FloatLabel>
          </div>
          <div class="form-group">
            <FloatLabel variant="in">
              <InputText id="edit_live_link" v-model="currentRecord.live_link"/>
              <label for="edit_live_link">Live Link (FB/Youtube)</label>
            </FloatLabel>
          </div>
          <div class="form-group">
            <FloatLabel variant="in">
              <InputText id="edit_congressional_report_link" v-model="currentRecord.congressional_report_link"/>
              <label for="edit_congressional_report_link">Congressional Report Link</label>
            </FloatLabel>
          </div>
          <div class="form-group">
            <FloatLabel variant="in">
              <Select id="edit_committee_remarks" v-model="currentRecord.committee_remarks" :options="committeeRemarksOptions" optionLabel="label" optionValue="value" />
              <label for="edit_committee_remarks">Committee Remarks</label>
            </FloatLabel>
          </div>
        </div>

        <!-- Actual Attendees Table -->
        <div class="section">
          <div class="section-header">
            <h3>Actual Attendees</h3>
            <Button icon="pi pi-plus" label="Add" size="small" @click="addAttendeeRow" />
          </div>
          <DataTable :value="actualAttendees" showGridlines size="small">
            <Column field="agency_code" header="Agency/Office">
              <template #body="{ data }">
                <Select v-model="data.agency_code" :options="agencyOptions" optionLabel="name" optionValue="name" class="w-full" filter />
              </template>
            </Column>
            <Column field="names" header="Name/s">
              <template #body="{ data }">
                <Textarea v-model="data.names" class="w-full" rows="1" autoResize />
              </template>
            </Column>
            <Column header="" style="width: 4rem">
              <template #body="{ data, index }">
                <Button icon="pi pi-trash" severity="danger" size="small" text @click="actualAttendees.splice(index, 1)" />
              </template>
            </Column>
          </DataTable>
        </div>

        <!-- Key Points Discussed Table -->
        <div class="section">
          <div class="section-header">
            <h3>Key Points Discussed</h3>
            <Button icon="pi pi-plus" label="Add" size="small" @click="addKeyPointRow" />
          </div>
          <DataTable :value="keyPointsDiscussed" showGridlines size="small">
            <Column field="description" header="Key Point">
              <template #body="{ data }">
                <Textarea v-model="data.description" class="w-full" rows="2" autoResize />
              </template>
            </Column>
            <Column header="" style="width: 4rem">
              <template #body="{ data, index }">
                <Button icon="pi pi-trash" severity="danger" size="small" text @click="keyPointsDiscussed.splice(index, 1)" />
              </template>
            </Column>
          </DataTable>
        </div>

        <!-- Status and Action Item Table -->
        <!---<div class="section">
          <div class="section-header">
            <h3>Status and Action Item</h3>
            <Button icon="pi pi-plus" label="Add" size="small" @click="addActionRow" />
          </div>
          <DataTable :value="actionItems" showGridlines size="small">
            <Column field="status" header="Status">
              <template #body="{ data }">
                <InputText v-model="data.status" class="w-full" />
              </template>
            </Column>
            <Column field="action_item" header="Action Item">
              <template #body="{ data }">
                <InputText v-model="data.action_item" class="w-full" />
              </template>
            </Column>
            <Column header="" style="width: 4rem">
              <template #body="{ data, index }">
                <Button icon="pi pi-trash" severity="danger" size="small" text @click="actionItems.splice(index, 1)" />
              </template>
            </Column>
          </DataTable>
        </div>-->

        <!-- Legislative Status Table -->
        <!---<div class="section">
          <div class="section-header">
            <h3>Legislative Status</h3>
            <Button icon="pi pi-plus" label="Add" size="small" @click="addLegislativeRow" />
          </div>
          <DataTable :value="legislativeStatuses" showGridlines size="small">
            <Column field="status" header="Status">
              <template #body="{ data }">
                <Select v-model="data.status" :options="legislativeStatusOptions" optionLabel="label" optionValue="value" class="w-full" />
              </template>
            </Column>
            <Column field="date" header="Date">
              <template #body="{ data }">
                <DatePicker v-model="data.date" dateFormat="yy-mm-dd" class="w-full" />
              </template>
            </Column>
            <Column header="" style="width: 4rem">
              <template #body="{ data, index }">
                <Button icon="pi pi-trash" severity="danger" size="small" text @click="legislativeStatuses.splice(index, 1)" />
              </template>
            </Column>
          </DataTable>
        </div>-->
      </div>

      <div class="modal-actions-right">
        <Button label="Save Changes" icon="pi pi-save" @click="saveAgendaChanges" :loading="saving" />
      </div>
    </Dialog>

    <Dialog v-model:visible="showReassignModal" header="Re-assign Liaison Officer" :modal="true" :style="{width: '450px'}">
      <div class="reassign-form">
        <p>Agenda: <strong>{{ reassignRecord?.agenda }}</strong></p>
        <div class="form-group">
          <FloatLabel variant="in">
            <Select id="reassign_lo" v-model="reassignLoId" :options="loUsers" optionLabel="name" optionValue="id" filter class="w-full" />
            <label for="reassign_lo">Assign to LO</label>
          </FloatLabel>
        </div>
      </div>
      <div class="modal-actions">
        <Button type="button" label="Cancel" severity="secondary" icon="pi pi-times" @click="showReassignModal = false" />
        <Button type="button" label="Re-assign" icon="pi pi-check" :loading="reassigning" @click="saveReassign" />
      </div>
    </Dialog>
  </div>
</template>


<script setup>
import { ref, computed, onMounted, getCurrentInstance } from 'vue'
import Swal from 'sweetalert2'
import api from '../axios.js'

const { proxy } = getCurrentInstance()

const records = ref([])
const showModal = ref(false)
const saving = ref(false)
const loading = ref(false)
const editLoading = ref(null)
const currentRecord = ref({})
const actionItems = ref([])
const legislativeStatuses = ref([])
const actualAttendees = ref([])
const keyPointsDiscussed = ref([])
const agencyOptions = ref([])
const actionSaving = ref(null)
const legislativeSaving = ref(null)
const attendeeSaving = ref(null)
const keyPointSaving = ref(null)
const loUsers = ref([])
const showReassignModal = ref(false)
const reassignRecord = ref(null)
const reassignLoId = ref(null)
const reassigning = ref(false)

const isLldDc = computed(() => proxy.$authStore.user?.role?.includes('pllg-dc-liaison'))
const canReassign = computed(() => {
  const roles = proxy.$authStore.user?.role || []
  return roles.includes('pllg-dc-liaison') || roles.includes('lo-staff')
})

const filters = ref({
  global: { value: null, matchMode: 'contains' }
})

const congressFilterOptions = [
  { label: '20th Congress', value: '20' },
  { label: '19th Congress', value: '19' }
]

const viewMode = ref('table')
const filterCongress = ref('20')
const filterRequestingParty = ref(null)
const filterCommittee = ref(null)
const filterLoggedBy = ref(null)
const filterLO = ref(null)
const filterAO = ref(null)
const filterSubjectTitle = ref(null)
const filterStatus = ref(null)
const loCurrentMonth = ref(new Date())

const loStatusOptions = [
  { label: 'Pending', value: 'pending' },
  { label: 'Completed', value: 'completed' }
]

const loRequestingPartyOptions = computed(() => {
  const parties = [...new Set(records.value.map(r => r.requesting_party).filter(Boolean))]
  return parties.sort().map(p => ({ label: p, value: p }))
})

const loCommitteeOptions = computed(() => {
  const committees = [...new Set(records.value.map(r => r.committee_ngas_op).filter(Boolean))]
  return committees.sort().map(c => ({ label: c, value: c }))
})

const loggedByOptions = computed(() => {
  const officers = [...new Set(records.value.map(r => r.logged_by).filter(Boolean))]
  return officers.sort().map(o => ({ label: o, value: o }))
})

const loOfficerOptions = computed(() => {
  const officers = [...new Set(records.value.map(r => r.assigned_lo_name).filter(Boolean))]
  return officers.sort().map(o => ({ label: o, value: o }))
})

const aoOfficerOptions = computed(() => {
  const officers = [...new Set(records.value.map(r => r.account_officer_name).filter(Boolean))]
  return officers.sort().map(o => ({ label: o, value: o }))
})


const loSubjectTitleOptions = computed(() => {
  const titles = [...new Set(records.value.map(r => r.subject_title).filter(Boolean))]
  return titles.sort().map(t => ({ label: t, value: t }))
})

const filteredRecords = computed(() => {
  return records.value.filter(r => {
    if (filterCongress.value && r.congress_period !== filterCongress.value) return false
    if (filterRequestingParty.value && r.requesting_party !== filterRequestingParty.value) return false
    if (filterCommittee.value && r.committee_ngas_op !== filterCommittee.value) return false
    if (filterLoggedBy.value && r.logged_by !== filterLoggedBy.value) return false
    if (filterLO.value && r.assigned_lo_name !== filterLO.value) return false
    if (filterAO.value && r.account_officer_name !== filterAO.value) return false
    if (filterSubjectTitle.value && r.subject_title !== filterSubjectTitle.value) return false
    if (filterStatus.value) {
      if (filterStatus.value === 'pending' && r.has_key_points) return false
      if (filterStatus.value === 'completed' && !r.has_key_points) return false
    }
    return true
  })
})

const hasActiveLoFilters = computed(() => {
  return filterRequestingParty.value || filterCommittee.value || filterLO.value || filterSubjectTitle.value || filterStatus.value
})

const clearLoFilters = () => {
  filterRequestingParty.value = null
  filterCommittee.value = null
  filterLO.value = null
  filterLoggedBy.value = null
  filterAO.value = null
  filterSubjectTitle.value = null
  filterStatus.value = null
}

// Two Weeks View
const loTwoWeekDays = computed(() => {
  const days = []
  const today = new Date()
  const startOfWeek = new Date(today)
  startOfWeek.setDate(today.getDate() - today.getDay() + 1)

  for (let i = 0; i < 10; i++) {
    const d = new Date(startOfWeek)
    d.setDate(startOfWeek.getDate() + Math.floor(i / 5) * 7 + (i % 5))
    const isoDate = d.toISOString().split('T')[0]
    const items = filteredRecords.value.filter(r => r.hearing_date_raw === isoDate)
    days.push({
      date: isoDate,
      dayName: d.toLocaleDateString('en-US', { weekday: 'short' }),
      dateFormatted: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      isToday: isoDate === today.toISOString().split('T')[0],
      items
    })
  }
  return days
})

// Month View
const loCurrentMonthName = computed(() => loCurrentMonth.value.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }))

const loMonthDays = computed(() => {
  const year = loCurrentMonth.value.getFullYear()
  const month = loCurrentMonth.value.getMonth()
  const firstDay = new Date(year, month, 1)
  const lastDay = new Date(year, month + 1, 0)
  const today = new Date().toISOString().split('T')[0]
  let startDow = firstDay.getDay() - 1
  if (startDow < 0) startDow = 6
  const days = []
  for (let i = 0; i < startDow; i++) days.push({ date: null })
  for (let d = 1; d <= lastDay.getDate(); d++) {
    const date = new Date(year, month, d)
    if (date.getDay() === 0 || date.getDay() === 6) continue
    const isoDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
    const items = filteredRecords.value.filter(r => r.hearing_date_raw === isoDate)
    days.push({ date: isoDate, dayNum: d, isToday: isoDate === today, items })
  }
  return days
})

const loPrevMonth = () => { const d = new Date(loCurrentMonth.value); d.setMonth(d.getMonth() - 1); loCurrentMonth.value = d }
const loNextMonth = () => { const d = new Date(loCurrentMonth.value); d.setMonth(d.getMonth() + 1); loCurrentMonth.value = d }

// Metrics
const buildLoMetric = (data, field, limit) => {
  const counts = {}
  data.forEach(r => { const val = r[field] || 'Unknown'; counts[val] = (counts[val] || 0) + 1 })
  let sorted = Object.entries(counts).sort((a, b) => b[1] - a[1])
  if (limit) sorted = sorted.slice(0, limit)
  const max = sorted.length > 0 ? sorted[0][1] : 1
  return sorted.map(([label, count]) => ({ label, count, percent: (count / max) * 100 }))
}

const loMetricsCongress = computed(() => buildLoMetric(filteredRecords.value, 'congress_period'))
const loMetricsRequestingParty = computed(() => buildLoMetric(filteredRecords.value, 'requesting_party'))
const loMetricsCommittee = computed(() => buildLoMetric(filteredRecords.value, 'committee_ngas_op', 10))
const loMetricsLO = computed(() => buildLoMetric(filteredRecords.value, 'assigned_lo_name'))

const legislativeStatusOptions = [
  { label: 'Filed', value: 'Filed' },
  { label: 'Pending in Committee', value: 'Pending in Committee' },
  { label: 'Approved on 2nd Reading', value: 'Approved on 2nd Reading' },
  { label: 'Approved on 3rd Reading', value: 'Approved on 3rd Reading' },
  { label: 'Transmitted to Senate', value: 'Transmitted to Senate' },
  { label: 'Transmitted to House', value: 'Transmitted to House' },
  { label: 'Ratified', value: 'Ratified' },
  { label: 'Signed into Law', value: 'Signed into Law' },
  { label: 'Vetoed', value: 'Vetoed' }
]

const billCategoryOptions = [
  { label: 'RED Bill', value: 'RED Bill' },
  { label: 'LEDAC', value: 'LEDAC' },
  { label: 'Non-LEDAC', value: 'Non-LEDAC' },
  { label: 'DOF Priority', value: 'DOF Priority' },
  { label: 'Others', value: 'Others' }
]

const positionPaperStatusOptions = [
  { label: 'Submitted', value: 'Submitted' },
  { label: 'Pending', value: 'Pending' },
  { label: 'Not Required', value: 'Not Required' }
]

const dofPositionOptions = [
  { label: 'Deferred', value: 'Deferred'},
  { label: 'Deferred with comments', value: 'Deferred with comments'},
  { label: 'Deferred with reservations', value: 'Deferred with reservations'},
  { label: 'Do not support', value: 'Do not support'},
  { label: 'Provisions are no longer necessary', value: 'Provisions are no longer necessary'},
  { label: 'Support', value: 'Support'},
  { label: 'With comments', value: 'With comments'},
  { label: 'With recommendations', value: 'With recommendations'},
  { label: 'With reservations', value: 'With reservations'},
]

const dofActionsOptions = [
  { label: 'DOF position paper has been signed and submitted', value: 'DOF position paper has been signed and submitted' },
  { label: 'DOF to attend TWG discussion', value: 'DOF to attend TWG discussion' },
  { label: 'For submission of cost-benefit analysis', value: 'For submission of cost-benefit analysis' },
  { label: 'For submission of data', value: 'For submission of data' },
  { label: 'For submission of data/information request', value: 'For submission of data/information request' },
  { label: 'For submission of data/study', value: 'For submission of data/study' },
  { label: 'For submission of DOF position paper', value: 'For submission of DOF position paper' },
  { label: 'For submission of DOF position paper and requested data', value: 'For submission of DOF position paper and requested data' },
  { label: 'For submission of joint position paper', value: 'For submission of joint position paper' },
  { label: 'For submission of proposed language', value: 'For submission of proposed language' },
  { label: 'For submission of study', value: 'For submission of study' },
  { label: 'For submission of suspension order', value: 'For submission of suspension order' },
  { label: 'For submission of updated DOF position paper', value: 'For submission of updated DOF position paper' },
  { label: 'Referred but no DOF Concern', value: 'Referred but no DOF Concern' },
  { label: 'Other', value: 'Other' },
]

const statusOfDeliverablesOptions = [
  { label: 'Ongoing', value: 'Ongoing'},
  { label: 'No deliverables', value: 'No deliverables'},
  { label: 'Deliverables submitted', value: 'Deliverables submitted'},
]

const typeOfMeetingOptions = [
 { label: 'Committee hearing', value: 'Committee hearing' },
 { label: 'Briefing / Consultative Meeting', value: 'Briefing / Consultative Meeting' },
 { label: 'TWG', value: 'TWG' },
 { label: 'Pre-bicam', value: 'Pre-bicam' },
 { label: 'Bicam', value: 'Bicam' },
]

const committeeRemarksOptions = [
  { label: "Agenda item was not undertaken.", value: "Agenda item was not undertaken."},
  { label: "Approved by secondary committee; for approval of primary committee", value: "Approved by secondary committee; for approval of primary committee" },
  { label: "Approved by the committee", value: "Approved by the committee" },
  { label: "Approved on 2nd reading", value: "Approved on 2nd reading" },
  { label: "Briefing suspended", value: "Briefing suspended" },
  { label: "Committee hearing adjourned", value: "Committee hearing adjourned" },
  { label: "Committee hearing suspended", value: "Committee hearing suspended" },
  { label: "Committee report approved and submitted", value: "Committee report approved and submitted" },
  { label: "Committee report approved and submitted. Pending approval on 2nd reading.", value: "Committee report approved and submitted. Pending approval on 2nd reading." },
  { label: "For approval in the committee", value: "For approval in the committee" },
  { label: "For bicameral confrence committee meeting", value: "For bicameral confrence committee meeting" },
  { label: "For committee report", value: "For committee report" },
  { label: "For finalization of committee report", value: "For finalization of committee report" },
  { label: "For further committee hearing", value: "For further committee hearing" },
  { label: "For further TWG discussion", value: "For further TWG discussion" },
  { label: "For TWG discussion", value: "For TWG discussion" },
  { label: "None", value: "None" },
  { label: "Pending approval on 2nd reading (for interpellation)", value: "Pending approval on 2nd reading (for interpellation)" },
  { label: "Pending approval on 2nd reading (for sponsorship)", value: "Pending approval on 2nd reading (for sponsorship)" },
  { label: "Referred to secondary committee (Appropriations)", value: "Referred to secondary committee (Appropriations)" },
  { label: "Referred to secondary committee (WAM)", value: "Referred to secondary committee (WAM)" },
  { label: "Referred to sub committees", value: "Referred to sub committees" },
  { label: "The committee hearing was followed by an executive session", value: "The committee hearing was followed by an executive session" },
  { label: "The TWG report will be submitted to the mother committee", value: "The TWG report will be submitted to the mother committee" },
  { label: "TWG adjourned", value: "TWG adjourned" },
  { label: "TWG adjourned, for finalization of committee report", value: "TWG adjourned, for finalization of committee report" },
  { label: "TWG discussion suspended.", value: "TWG discussion suspended." },
  { label: "Unnumbered substitute bill and committee report approved by the committee", value: "Unnumbered substitute bill and committee report approved by the committee" },
  { label: "Unnumbered substitute bill approved by the committee", value: "Unnumbered substitute bill approved by the committee" }
]

const loadRecords = async () => {
  try {
    loading.value = true
    const response = await api.get('/lo-tracker')
    records.value = response.data
  } catch (error) {
    console.error('Error loading records:', error)
  } finally {
    loading.value = false
  }
}

const loadAgencies = async () => {
  try {
    const response = await api.get('/agencies')
    agencyOptions.value = response.data
  } catch (error) {
    console.error('Error loading agencies:', error)
  }
}

const loadLoUsers = async () => {
  try {
    const response = await api.get('/lo-officers')
    loUsers.value = response.data
  } catch (error) {
    console.error('Error loading LO users:', error)
  }
}

const openReassignModal = (record) => {
  reassignRecord.value = record
  reassignLoId.value = record.assigned_lo
  showReassignModal.value = true
}

const saveReassign = async () => {
  if (!reassignLoId.value) return
  const result = await Swal.fire({
    title: 'Re-assign LO?',
    text: 'Are you sure you want to re-assign this item to a different Liaison Officer?',
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: 'Re-assign',
    cancelButtonText: 'Cancel'
  })
  if (!result.isConfirmed) return

  try {
    reassigning.value = true
    await api.put(`/lo-tracker/${reassignRecord.value.id}/reassign`, {
      assigned_lo: reassignLoId.value
    })
    showReassignModal.value = false
    loadRecords()
    Swal.fire({ title: 'Saved!', text: 'LO re-assigned successfully', icon: 'success', timer: 1500, showConfirmButton: false })
  } catch (error) {
    Swal.fire({ title: 'Error!', text: 'Failed to re-assign', icon: 'error' })
  } finally {
    reassigning.value = false
  }
}

const getAgencyName = (id) => {
  const agency = agencyOptions.value.find(a => a.id === id)
  return agency ? agency.name : '—'
}

const formatAttendeeNames = (names) => {
  if (!names) return ''
  // Split on patterns like "1.", "2.", "3." etc. and add line breaks
  return names.replace(/(\d+\.)\s*/g, '<br>$1 ').trim().replace(/^<br>/, '')
}

const formatReferrals = (referrals) => {
  if (!referrals || !referrals.length) return '—'
  return referrals.map(code => {
    const agency = agencyOptions.value.find(a => a.code === code)
    return agency ? agency.name : code
  }).join(', ')
}

const openEditModal = async (record) => {
  editLoading.value = record.id
  try {
    const response = await api.get(`/lo-tracker/${record.id}`)
    currentRecord.value = response.data
    actionItems.value = (response.data.action_items || []).map(item => ({ ...item, editing: false }))
    legislativeStatuses.value = (response.data.legislative_statuses || []).map(item => ({ ...item, editing: false }))
    actualAttendees.value = (response.data.attendees || []).map(item => ({ ...item, editing: false }))
    keyPointsDiscussed.value = (response.data.key_points || []).map(item => ({ ...item, editing: false }))
    showModal.value = true
  } catch (error) {
    console.error('Error loading record:', error)
  } finally {
    editLoading.value = null
  }
}

const closeModal = () => {
  showModal.value = false
}

const saveAgendaChanges = async () => {
  try {
    saving.value = true
    await api.put(`/lo-tracker/${currentRecord.value.id}`, {
      agenda: currentRecord.value.agenda,
      agenda_link: currentRecord.value.agenda_link,
      long_title: currentRecord.value.long_title,
      short_title: currentRecord.value.short_title,
      bill_category: currentRecord.value.bill_category,
      dof_position: currentRecord.value.dof_position,
      dof_actions_to_be_taken: currentRecord.value.dof_actions_to_be_taken,
      deadline_of_deliverables: currentRecord.value.deadline_of_deliverables,
      status_of_dof_deliverables: currentRecord.value.status_of_dof_deliverables,
      additional_remarks: currentRecord.value.additional_remarks,
      pending_tasks: currentRecord.value.pending_tasks,
      type_of_meeting: currentRecord.value.type_of_meeting,
      live_link: currentRecord.value.live_link,
      congressional_report_link: currentRecord.value.congressional_report_link,
      committee_remarks: currentRecord.value.committee_remarks,
      // action_items: actionItems.value.map(item => ({ id: item.id || null, status: item.status, action_item: item.action_item })),
      // legislative_statuses: legislativeStatuses.value.map(item => ({ id: item.id || null, status: item.status, date: item.date })),
      attendees: actualAttendees.value.map(item => ({ id: item.id || null, agency_code: item.agency_code, names: item.names })),
      key_points: keyPointsDiscussed.value.map(item => ({ id: item.id || null, description: item.description })),
    })
    loadRecords()
    Swal.fire({ title: 'Saved!', text: 'All changes saved successfully', icon: 'success' })
  } catch (error) {
    Swal.fire({ title: 'Error!', text: 'Failed to save changes', icon: 'error' })
  } finally {
    saving.value = false
  }
}

// ============================================================================
// ACTION ITEMS CRUD
// ============================================================================

const addActionRow = () => {
  actionItems.value.push({ status: '', action_item: '' })
}

const editActionRow = (index) => {
  actionItems.value[index]._backup = { status: actionItems.value[index].status, action_item: actionItems.value[index].action_item }
  actionItems.value[index].editing = true
}

const cancelActionRow = (index) => {
  if (actionItems.value[index].isNew) {
    actionItems.value.splice(index, 1)
  } else {
    const backup = actionItems.value[index]._backup
    actionItems.value[index].status = backup.status
    actionItems.value[index].action_item = backup.action_item
    actionItems.value[index].editing = false
  }
}

const saveActionRow = async (data, index) => {
  try {
    actionSaving.value = index
    if (data.isNew) {
      const response = await api.post(`/lo-tracker/${currentRecord.value.id}/action-items`, {
        status: data.status,
        action_item: data.action_item
      })
      actionItems.value[index] = { ...response.data, editing: false }
    } else {
      await api.put(`/lo-tracker/${currentRecord.value.id}/action-items/${data.id}`, {
        status: data.status,
        action_item: data.action_item
      })
      actionItems.value[index].editing = false
    }
    delete actionItems.value[index].isNew
    delete actionItems.value[index]._backup
  } catch (error) {
    Swal.fire({ title: 'Error!', text: 'Failed to save', icon: 'error' })
  } finally {
    actionSaving.value = null
  }
}

const deleteActionRow = async (index) => {
  const item = actionItems.value[index]
  const result = await Swal.fire({
    title: 'Delete?',
    text: 'Are you sure?',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Delete',
    confirmButtonColor: '#ef4444'
  })
  if (result.isConfirmed) {
    try {
      await api.delete(`/lo-tracker/${currentRecord.value.id}/action-items/${item.id}`)
      actionItems.value.splice(index, 1)
    } catch (error) {
      Swal.fire({ title: 'Error!', text: 'Failed to delete', icon: 'error' })
    }
  }
}

// ============================================================================
// ACTUAL ATTENDEES CRUD
// ============================================================================

const addAttendeeRow = () => {
  actualAttendees.value.push({ agency_code: null, names: '' })
}

const editAttendeeRow = (index) => {
  actualAttendees.value[index]._backup = { agency_code: actualAttendees.value[index].agency_code, name: actualAttendees.value[index].names }
  actualAttendees.value[index].editing = true
}

const cancelAttendeeRow = (index) => {
  if (actualAttendees.value[index].isNew) {
    actualAttendees.value.splice(index, 1)
  } else {
    const backup = actualAttendees.value[index]._backup
    actualAttendees.value[index].agency_code = backup.agency_code
    actualAttendees.value[index].names = backup.names
    actualAttendees.value[index].editing = false
  }
}

const saveAttendeeRow = async (data, index) => {
  try {
    attendeeSaving.value = index
    if (data.isNew) {
      const response = await api.post(`/lo-tracker/${currentRecord.value.id}/actual-attendees`, {
        agency_code: data.agency_code,
        names: data.names
      })
      actualAttendees.value[index] = { ...response.data, editing: false }
    } else {
      await api.put(`/lo-tracker/${currentRecord.value.id}/actual-attendees/${data.id}`, {
        agency_code: data.agency_code,
        names: data.names
      })
      actualAttendees.value[index].editing = false
    }
    delete actualAttendees.value[index].isNew
    delete actualAttendees.value[index]._backup
  } catch (error) {
    Swal.fire({ title: 'Error!', text: 'Failed to save', icon: 'error' })
  } finally {
    attendeeSaving.value = null
  }
}

const deleteAttendeeRow = async (index) => {
  const item = actualAttendees.value[index]
  const result = await Swal.fire({
    title: 'Delete?',
    text: 'Are you sure?',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Delete',
    confirmButtonColor: '#ef4444'
  })
  if (result.isConfirmed) {
    try {
      await api.delete(`/lo-tracker/${currentRecord.value.id}/actual-attendees/${item.id}`)
      actualAttendees.value.splice(index, 1)
    } catch (error) {
      Swal.fire({ title: 'Error!', text: 'Failed to delete', icon: 'error' })
    }
  }
}

// ============================================================================
// KEY POINTS DISCUSSED CRUD
// ============================================================================

const addKeyPointRow = () => {
  keyPointsDiscussed.value.push({ description: '' })
}

const editKeyPointRow = (index) => {
  keyPointsDiscussed.value[index]._backup = { description: keyPointsDiscussed.value[index].description }
  keyPointsDiscussed.value[index].editing = true
}

const cancelKeyPointRow = (index) => {
  if (keyPointsDiscussed.value[index].isNew) {
    keyPointsDiscussed.value.splice(index, 1)
  } else {
    keyPointsDiscussed.value[index].description = keyPointsDiscussed.value[index]._backup.description
    keyPointsDiscussed.value[index].editing = false
  }
}

const saveKeyPointRow = async (data, index) => {
  try {
    keyPointSaving.value = index
    if (data.isNew) {
      const response = await api.post(`/lo-tracker/${currentRecord.value.id}/key-points`, {
        description: data.description
      })
      keyPointsDiscussed.value[index] = { ...response.data, editing: false }
    } else {
      await api.put(`/lo-tracker/${currentRecord.value.id}/key-points/${data.id}`, {
        description: data.description
      })
      keyPointsDiscussed.value[index].editing = false
    }
    delete keyPointsDiscussed.value[index].isNew
    delete keyPointsDiscussed.value[index]._backup
  } catch (error) {
    Swal.fire({ title: 'Error!', text: 'Failed to save', icon: 'error' })
  } finally {
    keyPointSaving.value = null
  }
}

const deleteKeyPointRow = async (index) => {
  const item = keyPointsDiscussed.value[index]
  const result = await Swal.fire({
    title: 'Delete?',
    text: 'Are you sure?',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Delete',
    confirmButtonColor: '#ef4444'
  })
  if (result.isConfirmed) {
    try {
      await api.delete(`/lo-tracker/${currentRecord.value.id}/key-points/${item.id}`)
      keyPointsDiscussed.value.splice(index, 1)
    } catch (error) {
      Swal.fire({ title: 'Error!', text: 'Failed to delete', icon: 'error' })
    }
  }
}

// ============================================================================
// LEGISLATIVE STATUS CRUD
// ============================================================================

const addLegislativeRow = () => {
  legislativeStatuses.value.push({ status: '', date: null })
}

const editLegislativeRow = (index) => {
  legislativeStatuses.value[index]._backup = { status: legislativeStatuses.value[index].status, date: legislativeStatuses.value[index].date }
  legislativeStatuses.value[index].editing = true
}

const cancelLegislativeRow = (index) => {
  if (legislativeStatuses.value[index].isNew) {
    legislativeStatuses.value.splice(index, 1)
  } else {
    const backup = legislativeStatuses.value[index]._backup
    legislativeStatuses.value[index].status = backup.status
    legislativeStatuses.value[index].date = backup.date
    legislativeStatuses.value[index].editing = false
  }
}

const saveLegislativeRow = async (data, index) => {
  try {
    legislativeSaving.value = index
    if (data.isNew) {
      const response = await api.post(`/lo-tracker/${currentRecord.value.id}/legislative-statuses`, {
        status: data.status,
        date: data.date
      })
      legislativeStatuses.value[index] = { ...response.data, editing: false }
    } else {
      await api.put(`/lo-tracker/${currentRecord.value.id}/legislative-statuses/${data.id}`, {
        status: data.status,
        date: data.date
      })
      legislativeStatuses.value[index].editing = false
    }
    delete legislativeStatuses.value[index].isNew
    delete legislativeStatuses.value[index]._backup
  } catch (error) {
    Swal.fire({ title: 'Error!', text: 'Failed to save', icon: 'error' })
  } finally {
    legislativeSaving.value = null
  }
}

const deleteLegislativeRow = async (index) => {
  const item = legislativeStatuses.value[index]
  const result = await Swal.fire({
    title: 'Delete?',
    text: 'Are you sure?',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Delete',
    confirmButtonColor: '#ef4444'
  })
  if (result.isConfirmed) {
    try {
      await api.delete(`/lo-tracker/${currentRecord.value.id}/legislative-statuses/${item.id}`)
      legislativeStatuses.value.splice(index, 1)
    } catch (error) {
      Swal.fire({ title: 'Error!', text: 'Failed to delete', icon: 'error' })
    }
  }
}

onMounted(() => {
  loadRecords()
  loadAgencies()
  if (canReassign.value) loadLoUsers()
})
</script>

<style scoped>
.page {
  padding: 1rem 0;
  background: var(--p-surface-50);
  min-height: 100vh;
}

.container {
  backdrop-filter: blur(10px);
  border-radius: 20px;
  margin: 1rem;
  padding: 1.5rem;
  width: calc(100vw - 2rem);
  max-width: none;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #1e3a8a;
}

.page-header h1 {
  color: #1e3a8a;
  font-size: 2rem;
  font-weight: 700;
}

.table-header {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 1rem;
}

.congress-filter {
  min-width: 200px;
}

.view-controls {
  margin-bottom: 1rem;
}

.view-buttons {
  display: flex;
  gap: 0.25rem;
}
.filter-select { min-width: 180px; }
.text-muted { color: #94a3b8; }

.attendee-chips { display: flex; flex-wrap: wrap; gap: 0.25rem; }
.attendee-chip { display: inline-block; padding: 0.15rem 0.5rem; background: #e0e7ff; color: #3730a3; border-radius: 12px; font-size: 0.75rem; font-weight: 600; }
.attendee-list { display: flex; flex-direction: column; gap: 0.5rem; }
.attendee-item { font-size: 0.85rem; line-height: 1.4; }
.attendee-code { font-weight: 700; color: #1e293b; }
.attendee-name { font-weight: 400; color: #1e293b; }
.attendee-no { color: #dc2626; font-weight: 600; }

.table-filters { display: flex; flex-direction: column; gap: 0.75rem; }
.filters-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 0.5rem; }
.search-field { grid-column: span 2; }
.search-field :deep(.p-inputtext) { height: 100%; }
.filter-item { width: 100% !important; }
.filter-summary { display: flex; justify-content: space-between; align-items: center; padding-top: 0.5rem; border-top: 1px solid #f1f5f9; }
.result-count { font-size: 0.8rem; color: #64748b; font-weight: 500; }

.calendar-view { background: white; border-radius: 8px; padding: 1.5rem; border: 1px solid #e2e8f0; }
.calendar-view h3 { color: #1e3a8a; margin-bottom: 1rem; text-align: center; }
.week-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 0.5rem; }
.day-card { border: 1px solid #e2e8f0; border-radius: 8px; padding: 0.5rem; min-height: 120px; }
.day-card.today { border-color: #3b82f6; background: #eff6ff; }
.day-header { display: flex; justify-content: space-between; font-size: 0.8rem; font-weight: 600; color: #64748b; margin-bottom: 0.5rem; padding-bottom: 0.25rem; border-bottom: 1px solid #f1f5f9; }
.day-events { display: flex; flex-direction: column; gap: 0.25rem; }
.day-event { font-size: 0.75rem; padding: 0.25rem 0.4rem; background: #dbeafe; border-radius: 4px; border-left: 3px solid #3b82f6; }
.event-committee { font-weight: 600; color: #1e3a8a; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.event-time { color: #64748b; font-size: 0.7rem; }
.no-events { font-size: 0.75rem; color: #cbd5e1; text-align: center; padding: 1rem 0; }

.month-header { display: flex; justify-content: center; align-items: center; gap: 1rem; margin-bottom: 1rem; }
.month-header h3 { margin: 0; min-width: 180px; text-align: center; }
.month-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 2px; }
.month-day-header { text-align: center; font-weight: 700; font-size: 0.8rem; color: #1e3a8a; padding: 0.5rem; background: #f1f5f9; }
.month-day-cell { border: 1px solid #f1f5f9; min-height: 80px; padding: 0.25rem; font-size: 0.75rem; }
.month-day-cell.today { background: #eff6ff; border-color: #3b82f6; }
.month-day-cell.has-events { background: #f0fdf4; }
.month-day-num { font-weight: 700; color: #374151; font-size: 0.8rem; margin-bottom: 0.2rem; }
.month-event-dot { font-size: 0.65rem; padding: 0.1rem 0.3rem; background: #dbeafe; border-radius: 3px; margin-bottom: 1px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; color: #1e40af; }
.month-more { font-size: 0.65rem; color: #64748b; font-weight: 600; }

.metrics-view { background: white; border-radius: 8px; padding: 1.5rem; border: 1px solid #e2e8f0; }
.metrics-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1.5rem; }
.metric-card { border: 1px solid #e2e8f0; border-radius: 8px; padding: 1rem; }
.metric-card h4 { color: #1e3a8a; margin: 0 0 1rem 0; font-size: 0.95rem; }
.metric-bars { display: flex; flex-direction: column; gap: 0.5rem; }
.metric-bar-row { display: flex; align-items: center; gap: 0.5rem; }
.metric-label { font-size: 0.8rem; color: #374151; min-width: 80px; max-width: 150px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.metric-bar-container { flex: 1; height: 20px; background: #f1f5f9; border-radius: 4px; overflow: hidden; }
.metric-bar { height: 100%; background: linear-gradient(90deg, #1e3a8a, #3b82f6); border-radius: 4px; transition: width 0.3s; }
.metric-bar.pie-bar { background: linear-gradient(90deg, #10b981, #34d399); }
.metric-bar.stacked-bar { background: linear-gradient(90deg, #f59e0b, #fbbf24); }
.metric-bar.logged-bar { background: linear-gradient(90deg, #8b5cf6, #a78bfa); }
.metric-count { font-size: 0.8rem; font-weight: 700; color: #1e3a8a; min-width: 30px; text-align: right; }

.actions {
  display: flex;
  gap: 0.5rem;
}

.detail-grid {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 2rem;
  max-height: 75vh;
  overflow-y: auto;
  background: #f8fafc;
}

.detail-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.detail-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
}

.detail-value {
  font-size: 0.95rem;
  color: #1e293b;
  font-weight: 500;
}

.editable-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background: white;
  border-radius: 10px;
  padding: 1.25rem;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.editable-section .p-button {
  align-self: flex-start;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group .p-inputtext,
.form-group .p-textarea,
.form-group .p-select,
.form-group .p-multiselect,
.form-group .p-datepicker {
  width: 100% !important;
}

:deep(.p-select),
:deep(.p-multiselect) {
  min-height: 3rem;
}

:deep(.p-multiselect .p-multiselect-label) {
  padding-top: 1.25rem;
}

:deep(.p-multiselect-chip-item) {
  margin-top: 0.2rem;
}

:deep(.p-datatable .p-inputtext),
:deep(.p-datatable .p-textarea),
:deep(.p-datatable .p-select),
:deep(.p-datatable .p-datepicker) {
  width: 100% !important;
}

.form-row-2 {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}

.form-row-3 {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
}

@media (max-width: 992px) {
  .form-row-3, .form-row-2 {
    grid-template-columns: 1fr;
  }


}

.section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  background: white;
  border-radius: 10px;
  padding: 1.25rem;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #e2e8f0;
}

.section-header h3 {
  color: #1e3a8a;
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
}


.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.5rem;
  border-top: 1px solid #e5e7eb;
  background: #f8fafc;
}

.modal-actions-right {
  display: flex;
  justify-content: flex-end;
  padding: 1.5rem;
  border-top: 1px solid #e5e7eb;
  background: #f8fafc;
  position: sticky;
  bottom: 0;
  z-index: 10;
}

@media (max-width: 768px) {
  .detail-row {
    grid-template-columns: 1fr;
  }
}

.reassign-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem 0;
}

.reassign-form .p-select {
  width: 100% !important;
}
</style>
